(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-included-tabs]';
  const initializedRoots = new WeakSet();

  function initIncludedTabs(root) {
    if (!root || initializedRoots.has(root)) return;

    if (typeof window.EmblaCarousel !== 'function') {
      console.warn('Included Tabs: EmblaCarousel is not available.');
      return;
    }

    initializedRoots.add(root);

    const emblas = {};

    function updateLock(embla) {
      const locked = !embla.canScrollPrev() && !embla.canScrollNext();

      embla.rootNode().classList.toggle('is-locked', locked);
    }

    function initTab(key) {
      if (emblas[key]) return;

      const panel = root.querySelector(`[data-panel="${key}"]`);
      const carousel = panel?.querySelector('.dci-tabs__carousel');

      if (!carousel) return;

      const slideCount =
        carousel.querySelectorAll('.embla__slide').length;

      if (slideCount <= 2) {
        carousel.classList.add('is-locked');
        return;
      }

      const embla = window.EmblaCarousel(carousel, {
        align: 'start',
        containScroll: 'trimSnaps',
        watchResize: false,
      });

      embla.on('reInit', () => {
        updateLock(embla);
      });

      updateLock(embla);
      emblas[key] = embla;
    }

    root.querySelectorAll('[data-panel]').forEach((panel) => {
      const wasHidden = panel.hidden;

      if (wasHidden) {
        panel.hidden = false;
      }

      initTab(panel.dataset.panel);

      if (wasHidden) {
        panel.hidden = true;
      }
    });

    function equalizeCardHeights() {
      const cards = root.querySelectorAll('.dci-tabs__card');

      if (!cards.length) return;

      const panels = [...root.querySelectorAll('[data-panel]')];
      const hiddenState = panels.map((panel) => panel.hidden);

      panels.forEach((panel) => {
        panel.hidden = false;
      });

      Object.values(emblas).forEach((embla) => {
        embla?.reInit();
      });

      cards.forEach((card) => {
        card.style.minHeight = '0px';
      });

      let maxHeight = 0;

      cards.forEach((card) => {
        maxHeight = Math.max(maxHeight, card.offsetHeight);
      });

      cards.forEach((card) => {
        card.style.minHeight = `${maxHeight}px`;
      });

      panels.forEach((panel, index) => {
        panel.hidden = hiddenState[index];
      });
    }

    equalizeCardHeights();

    let resizeTimer;

    window.addEventListener(
      'resize',
      () => {
        window.clearTimeout(resizeTimer);

        resizeTimer = window.setTimeout(
          equalizeCardHeights,
          150
        );
      },
      { passive: true }
    );

    if (document.fonts?.ready) {
      document.fonts.ready.then(equalizeCardHeights);
    }

    root.querySelectorAll('[data-tab]').forEach((button) => {
      button.addEventListener('click', () => {
        const key = button.dataset.tab;

        root.querySelectorAll('[data-tab]').forEach((item) => {
          item.classList.toggle(
            'is-active',
            item === button
          );
        });

        root.querySelectorAll('[data-panel]').forEach((panel) => {
          panel.hidden = panel.dataset.panel !== key;
        });

        initTab(key);
        emblas[key]?.reInit();
      });
    });

    const videosContainer =
      root.querySelector('.dci-tabs__videos');

    if (videosContainer) {
      const thumbnailVideos =
        videosContainer.querySelectorAll('.dci-tabs__video');

      function loadAndPlay(video) {
        if (video.dataset.poster) {
          video.poster = video.dataset.poster;
          video.removeAttribute('data-poster');
        }

        if (!video.dataset.src) return;

        video.src = video.dataset.src;
        video.removeAttribute('data-src');
        video.load();

        video.play().catch(() => {});
      }

      if (!('IntersectionObserver' in window)) {
        thumbnailVideos.forEach(loadAndPlay);
      } else if (thumbnailVideos.length) {
        const videoObserver = new IntersectionObserver(
          (entries, observer) => {
            const isVisible = entries.some(
              (entry) => entry.isIntersecting
            );

            if (!isVisible) return;

            thumbnailVideos.forEach(loadAndPlay);
            observer.disconnect();
          },
          {
            rootMargin: '400px 0px',
          }
        );

        videoObserver.observe(videosContainer);
      }

      const count = Number.parseInt(
        videosContainer.dataset.videoCount || '0',
        10
      );

      const mediaQuery = window.matchMedia(
        '(max-width: 768px)'
      );

      let thumbnailEmbla = null;

      function setupThumbnailCarousel() {
        if (count <= 3) return;

        if (mediaQuery.matches && !thumbnailEmbla) {
          thumbnailEmbla = window.EmblaCarousel(
            videosContainer,
            {
              align: 'start',
              containScroll: 'trimSnaps',
            }
          );

          videosContainer.classList.add(
            'embla-initialized'
          );
        } else if (!mediaQuery.matches && thumbnailEmbla) {
          thumbnailEmbla.destroy();
          thumbnailEmbla = null;

          videosContainer.classList.remove(
            'embla-initialized'
          );
        }
      }

      setupThumbnailCarousel();

      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener(
          'change',
          setupThumbnailCarousel
        );
      } else {
        mediaQuery.addListener(setupThumbnailCarousel);
      }
    }

    const overlay = root.querySelector('.dc-video-popup');

    if (!overlay) return;

    const triggers =
      root.querySelectorAll('.dci-tabs__video-link');

    let popupEmbla = null;
    let currentVideo = null;
    let currentBar = null;
    let animationFrameId = null;
    let isMuted = true;

    const popupVideos = () =>
      overlay.querySelectorAll('.dc-video-popup__video');

    const progressBars = () =>
      overlay.querySelectorAll(
        '.dc-video-popup__progress-bar'
      );

    function syncMuteButtons() {
      overlay
        .querySelectorAll('.dc-video-popup__mute')
        .forEach((button) => {
          button.dataset.muted = String(isMuted);

          button.setAttribute(
            'aria-label',
            isMuted ? 'Unmute video' : 'Mute video'
          );
        });
    }

    function updateProgress() {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }

      function step() {
        if (
          currentVideo &&
          currentBar &&
          Number.isFinite(currentVideo.duration)
        ) {
          const progress =
            (currentVideo.currentTime /
              currentVideo.duration) *
            100;

          currentBar.style.width = `${progress}%`;

          if (currentVideo.ended && popupEmbla) {
            const total = popupVideos().length;

            if (
              popupEmbla.selectedScrollSnap() <
              total - 1
            ) {
              popupEmbla.scrollNext();
            } else {
              popupEmbla.scrollTo(0, true);
            }

            return;
          }
        }

        animationFrameId =
          requestAnimationFrame(step);
      }

      step();
    }

    function playActive() {
      popupVideos().forEach((video) => {
        video.pause();
        video.currentTime = 0;
        video.muted = true;
      });

      progressBars().forEach((bar) => {
        bar.style.width = '0%';
      });

      const activeSlide =
        popupEmbla?.slideNodes()[
          popupEmbla.selectedScrollSnap()
        ];

      if (!activeSlide) return;

      currentVideo = activeSlide.querySelector(
        '.dc-video-popup__video'
      );

      currentBar = activeSlide.querySelector(
        '.dc-video-popup__progress-bar'
      );

      if (currentVideo) {
        currentVideo.muted = isMuted;

        currentVideo.play().catch(() => {});

        updateProgress();
      }
    }

    function initPopupEmbla() {
      if (popupEmbla) return;

      popupVideos().forEach((video) => {
        if (!video.dataset.src) return;

        video.src = video.dataset.src;
        video.removeAttribute('data-src');
      });

      const carousel = overlay.querySelector(
        '.dc-video-popup__carousel'
      );

      if (!carousel) return;

      popupEmbla = window.EmblaCarousel(carousel, {
        axis: 'y',
        align: 'start',
        containScroll: 'trimSnaps',
        breakpoints: {
          '(min-width: 769px)': {
            align: 'center',
            containScroll: false,
          },
        },
      });

      popupEmbla.on('select', playActive);
    }

    function openPopup(index) {
      overlay.hidden = false;
      document.body.style.overflow = 'hidden';

      initPopupEmbla();

      if (!popupEmbla) return;

      popupEmbla.reInit();
      popupEmbla.scrollTo(index, true);

      playActive();
    }

    function closePopup() {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
      }

      popupVideos().forEach((video) => {
        video.pause();
        video.currentTime = 0;
      });

      progressBars().forEach((bar) => {
        bar.style.width = '0%';
      });

      overlay.hidden = true;
      document.body.style.overflow = '';

      currentVideo = null;
      currentBar = null;
      isMuted = true;

      syncMuteButtons();
    }

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();

        const index = Number.parseInt(
          trigger.dataset.videoIndex || '0',
          10
        );

        openPopup(index);
      });
    });

    overlay.addEventListener('click', (event) => {
      const navButton = event.target.closest(
        '.dc-video-popup__nav-btn'
      );

      if (navButton && popupEmbla) {
        if (
          navButton.classList.contains(
            'dc-video-popup__nav-btn--prev'
          )
        ) {
          popupEmbla.scrollPrev();
        } else {
          popupEmbla.scrollNext();
        }

        return;
      }

      if (
        event.target.closest('.dc-video-popup__mute')
      ) {
        isMuted = !isMuted;

        popupVideos().forEach((video) => {
          video.muted = true;
        });

        if (!isMuted && currentVideo) {
          currentVideo.muted = false;
        }

        syncMuteButtons();
        return;
      }

      if (
        event.target.closest('.dc-video-popup__close')
      ) {
        closePopup();
        return;
      }

      if (
        event.target === overlay ||
        event.target.classList.contains(
          'embla__slide'
        ) ||
        event.target.classList.contains(
          'dc-video-popup__media'
        )
      ) {
        closePopup();
        return;
      }

      if (
        event.target.classList.contains(
          'dc-video-popup__video'
        ) &&
        popupEmbla
      ) {
        const slide =
          event.target.closest('.embla__slide');

        const slideIndex = slide
          ? popupEmbla.slideNodes().indexOf(slide)
          : -1;

        if (
          slideIndex !== -1 &&
          slideIndex !==
            popupEmbla.selectedScrollSnap()
        ) {
          popupEmbla.scrollTo(slideIndex);
        }
      }
    });

    document.addEventListener('keydown', (event) => {
      if (
        event.key === 'Escape' &&
        !overlay.hidden
      ) {
        closePopup();
      }
    });
  }

  function observeRoot(root) {
    if (
      !root ||
      root.dataset.dciObserved === 'true'
    ) {
      return;
    }

    root.dataset.dciObserved = 'true';

    if (!('IntersectionObserver' in window)) {
      initIncludedTabs(root);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const isVisible = entries.some(
          (entry) => entry.isIntersecting
        );

        if (!isVisible) return;

        observer.disconnect();
        initIncludedTabs(root);
      },
      {
        rootMargin: '500px 0px',
      }
    );

    observer.observe(root);
  }

  function boot(scope = document) {
    scope
      .querySelectorAll(ROOT_SELECTOR)
      .forEach(observeRoot);
  }

  if (!window.__dreamCurlsIncludedTabsLoaded) {
    window.__dreamCurlsIncludedTabsLoaded = true;

    if (document.readyState === 'loading') {
      document.addEventListener(
        'DOMContentLoaded',
        () => boot(),
        { once: true }
      );
    } else {
      boot();
    }

    document.addEventListener(
      'shopify:section:load',
      (event) => {
        if (
          event.target.matches?.(ROOT_SELECTOR)
        ) {
          observeRoot(event.target);
        } else {
          boot(event.target);
        }
      }
    );
  } else {
    boot();
  }
})();